import { useCallback, useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Search, ExternalLink, Plus, MapPin, Briefcase, Building2, Clock, PenTool, Target, Sparkles, X, Radar, Check, RefreshCw, Heart, FilterX, Video, Ban } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * Compact popover that captures a dismissal reason before firing
 * `hideJob(job, reason)`. Reasons flow into `hidden_jobs.reason` and
 * will eventually seed the match-coach with negative signals ("user
 * repeatedly dismissed low-salary → downweight similar vacancies").
 *
 * UX contract:
 *   - Ban icon opens the popover
 *   - Four quick-chip reasons (localised) — click = pick + auto-fire
 *   - Free-text input — Enter or "Verbergen" button submits typed value
 *   - "Overslaan" button hides with no reason (empty string)
 */
function HideReasonPopover({ job, onHide, testId, className, t }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  // Quick-picks live in i18n so they're translatable. Kept short so a
  // chip row wraps to at most 2 lines on tablet widths.
  const quickReasons = [
    t("jsHideReasonSalary"),
    t("jsHideReasonLocation"),
    t("jsHideReasonLevel"),
    t("jsHideReasonWorktime"),
  ];

  const submit = (reason) => {
    setOpen(false);
    setText("");
    onHide(job, reason);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          onClick={(e) => e.stopPropagation()}
          className={className}
          title={t("jsHideCta")}
          aria-label={t("jsHideCta")}
        >
          <Ban size={13} strokeWidth={1.75} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-72 p-3 bg-white border border-neutral-900 shadow-lg space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
            {t("jsHideReasonTitle")}
          </p>
          <p className="text-[0.72rem] text-neutral-600 mt-0.5 leading-snug">
            {t("jsHideReasonHint")}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-reasons`}>
          {quickReasons.map((r) => (
            <button
              key={r}
              type="button"
              data-testid={`${testId}-reason-${r.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
              onClick={() => submit(r)}
              className="inline-flex items-center border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white px-2.5 py-1 text-[0.7rem] leading-tight transition-colors"
            >
              {r}
            </button>
          ))}
        </div>
        <div className="space-y-1.5">
          <input
            type="text"
            data-testid={`${testId}-reason-input`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submit(text.trim()); }}
            placeholder={t("jsHideReasonPh")}
            maxLength={200}
            className="w-full border border-neutral-300 focus:border-neutral-900 outline-none px-2 py-1.5 text-sm"
          />
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              data-testid={`${testId}-reason-submit`}
              disabled={text.trim().length === 0}
              onClick={() => submit(text.trim())}
              className="flex-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] bg-neutral-900 hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed text-[#F9F8F6] px-3 py-1.5"
            >
              {t("jsHideReasonSubmit")}
            </button>
            <button
              type="button"
              data-testid={`${testId}-reason-skip`}
              onClick={() => submit("")}
              className="font-mono text-[0.6rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 px-3 py-1.5"
            >
              {t("jsHideReasonSkip")}
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}


import JobSourceBadge from "@/components/JobSourceBadge";
import MatchRadarInsights from "@/components/MatchRadarInsights";
import ThinkingLoader from "@/components/ThinkingLoader";
import VideoLetterModal from "@/components/VideoLetterModal";
import { PROMOTED_SOURCES } from "@/lib/jobSource";

/**
 * JobSearch panel — dashboard sidebar tab. Users type keywords + location,
 * (optionally) pick a branche-chip, see combined results from JSearch and
 * Adzuna, and can:
 *   1. Voeg toe — save the job to their tracker.
 *   2. + Motivatiebrief — save the job **and** open the letter wizard.
 *   3. Match met mijn CV — Gemini scores each result 0-100; the slider
 *      hides anything below the threshold (default 50 %).
 *
 * Props:
 *   onCreatedForLetter(job) — invoked after the "Add + Letter" flow.
 *   onJobCreated() — refresh the parent job list (optional).
 */
export default function JobSearchPanel({ onCreatedForLetter, onJobCreated }) {
  const { t, lang } = useT();
  // Restore last-used filters + last-fetched results from localStorage so
  // switching to another dashboard view doesn't wipe your search state.
  const savedFilters = (() => {
    try { return JSON.parse(localStorage.getItem("jg_jobsearch_filters_v1") || "{}"); }
    catch { return {}; }
  })();
  const savedResults = (() => {
    try {
      const raw = JSON.parse(localStorage.getItem("jg_jobsearch_results_v1") || "null");
      if (!raw || !raw.saved_at) return null;
      // Expire cached results after 6 hours so we don't show stale data.
      const ageMs = Date.now() - new Date(raw.saved_at).getTime();
      if (ageMs > 6 * 3600 * 1000) return null;
      return raw;
    } catch { return null; }
  })();
  const [q, setQ] = useState(savedFilters.q || "");
  const [location, setLocation] = useState(savedFilters.location || "Nederland");
  // Multi-select filter state — arrays of tags. Empty array = "Alle".
  const [selectedCategories, setSelectedCategories] = useState(Array.isArray(savedFilters.categories) ? savedFilters.categories : []);
  const [selectedEmploymentTypes, setSelectedEmploymentTypes] = useState(Array.isArray(savedFilters.employmentTypes) ? savedFilters.employmentTypes : []);
  const [results, setResults] = useState(savedResults?.results ?? null);
  const [sources, setSources] = useState(savedResults?.sources ?? null);
  const [busy, setBusy] = useState(false);
  const [addedIds, setAddedIds] = useState(new Set());
  const [rowBusy, setRowBusy] = useState(null);
  // Job detail modal — user clicks a result card to open a full-detail popup.
  const [detailJob, setDetailJob] = useState(null);
  // Favorites — persisted server-side, refreshed on mount + after add/remove.
  const [favorites, setFavorites] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [activeTab, setActiveTab] = useState("search"); // "search" | "favorites"
  // Video-pitch modal — opened when the user clicks "Video-pitch" on a
  // favorite row. Holds the source favorite so the modal can pre-load a
  // vacancy-specific script and upload with `favorite_id` set.
  const [pitchModalFav, setPitchModalFav] = useState(null);
  // Cue-cards cache for the vacancy-detail modal — { [external_id]: string[3] }.
  // Loaded lazily when the detail opens for a favorited job.
  const [cueCards, setCueCards] = useState({});
  const [cueCardsBusy, setCueCardsBusy] = useState({});

  // Branche chips
  const [categories, setCategories] = useState([]);

  // Skip-keywords blocklist — vacatures met deze woorden worden server-side
  // gefilterd (bijv. "HBO", "gecertificeerd"). We laden bij mount en
  // vernieuwen na iedere bewerking; de zoek-response geeft ook `skipped`
  // terug zodat we een badge tonen.
  const [skipKeywords, setSkipKeywords] = useState([]);
  const [skipInput, setSkipInput] = useState("");
  const [skipSaving, setSkipSaving] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);
  const [skippedCount, setSkippedCount] = useState(0);
  // Session-only bypass — set of lowercased skip-words that the user chose
  // to temporarily disable via the "Skip Word Undo" flow in Skip History.
  // Persist in sessionStorage so a dashboard-tab switch doesn't lose them,
  // but a full page reload restores the full blocklist as expected.
  const [bypassSkips, setBypassSkips] = useState(() => {
    try {
      const raw = sessionStorage.getItem("jg_skip_bypass_v1");
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr.map((s) => String(s).toLowerCase()) : []);
    } catch { return new Set(); }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem("jg_skip_bypass_v1", JSON.stringify([...bypassSkips]));
    } catch { /* quota exceeded — ignore */ }
  }, [bypassSkips]);
  // Metadata for the skip-reason tooltip — one entry per hidden vacancy
  // with the keyword(s) that matched. Populated by the search endpoint;
  // wiped whenever the user clears the blocklist or runs a fresh query.
  const [skippedItems, setSkippedItems] = useState([]);
  const [skipTipOpen, setSkipTipOpen] = useState(false);
  // Persistent skip-history log — populated when the "Geschiedenis" tab
  // is opened. Server keeps at most 500 rows / user.
  const [skipHistory, setSkipHistory] = useState(null);  // null → not loaded yet
  const [skipHistoryBusy, setSkipHistoryBusy] = useState(false);
  // Restore dialog — opens when the user clicks a Skip History row that
  // has matched skip-word(s). Lets them choose whether to just open the
  // URL, temporarily bypass the matched word(s) for this session, or
  // permanently remove them from the blocklist.
  const [restoreDialog, setRestoreDialog] = useState(null); // {row, matched: []}
  const loadSkipHistory = useCallback(async () => {
    setSkipHistoryBusy(true);
    try {
      const { data } = await api.get("/jobs-search/skip-history");
      setSkipHistory(data?.items || []);
    } catch {
      setSkipHistory([]);
    } finally {
      setSkipHistoryBusy(false);
    }
  }, []);
  useEffect(() => {
    if (activeTab === "history" && skipHistory === null) {
      loadSkipHistory();
    }
  }, [activeTab, skipHistory, loadSkipHistory]);
  const clearSkipHistory = async () => {
    if (!window.confirm(t("jsHistoryClearConfirm"))) return;
    setSkipHistoryBusy(true);
    try {
      await api.delete("/jobs-search/skip-history");
      setSkipHistory([]);
      toast.success(t("jsHistoryCleared"));
    } catch {
      toast.error(t("jsHistoryClearFail"));
    } finally {
      setSkipHistoryBusy(false);
    }
  };
  // Skip-word suggestions: {from_insights: [...], from_cv_gaps: [...]}.
  // Loaded lazily when the user opens the panel so we don't waste an API
  // hit on visitors who never touch the filter.
  const [skipSug, setSkipSug] = useState({ from_insights: [], from_cv_gaps: [] });
  const [skipSugLoading, setSkipSugLoading] = useState(false);
  useEffect(() => {
    api.get("/jobs-search/skip-keywords")
      .then(({ data }) => setSkipKeywords(data?.keywords || []))
      .catch(() => { /* ignore — feature stays inert until a save succeeds */ });
  }, []);
  const refreshSuggestions = useCallback(async () => {
    setSkipSugLoading(true);
    try {
      const { data } = await api.get("/jobs-search/skip-suggestions");
      setSkipSug({
        from_insights: data?.from_insights || [],
        from_cv_gaps: data?.from_cv_gaps || [],
      });
    } catch {
      setSkipSug({ from_insights: [], from_cv_gaps: [] });
    } finally {
      setSkipSugLoading(false);
    }
  }, []);
  const dismissSuggestion = async (keyword) => {
    // Optimistic: pull the chip off both lists immediately so the click
    // feels instant, then persist server-side.
    setSkipSug((cur) => ({
      from_insights: cur.from_insights.filter((k) => k !== keyword),
      from_cv_gaps: cur.from_cv_gaps.filter((k) => k !== keyword),
    }));
    try {
      await api.post("/jobs-search/skip-suggestions/dismiss", { keyword });
    } catch {
      // If persistence fails, refetch so the UI reflects true state.
      refreshSuggestions();
    }
  };
  // Fetch once when the panel first opens; refresh whenever the active
  // list changes so accepted suggestions vanish without a manual reload.
  useEffect(() => {
    if (skipOpen) refreshSuggestions();
  }, [skipOpen, skipKeywords, refreshSuggestions]);
  const saveSkipKeywords = async (next) => {
    setSkipSaving(true);
    try {
      const { data } = await api.put("/jobs-search/skip-keywords", { keywords: next });
      setSkipKeywords(data?.keywords || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("jsSkipSaveFail"));
    } finally {
      setSkipSaving(false);
    }
  };
  const addSkipKeyword = () => {
    const val = skipInput.trim();
    if (!val) return;
    const cur = new Set(skipKeywords.map((k) => k.toLowerCase()));
    if (cur.has(val.toLowerCase())) { setSkipInput(""); return; }
    const next = [...skipKeywords, val].slice(0, 30);
    saveSkipKeywords(next);
    setSkipInput("");
  };
  const removeSkipKeyword = (kw) => {
    saveSkipKeywords(skipKeywords.filter((k) => k !== kw));
  };

  // CV match state
  const [matches, setMatches] = useState(savedResults?.matches ?? null); // { [id]: { score, reason } }
  const [matchBusy, setMatchBusy] = useState(false);
  const [matchThreshold, setMatchThreshold] = useState(50);

  // CV match state — favorites tab. Keyed by external_id and persisted
  // server-side on `job_favorites.match_score / match_reason` so the badge
  // survives page reloads. Populated lazily from the `/favorites` GET.
  const [favMatches, setFavMatches] = useState({}); // { [external_id]: { score, reason } }
  const [favMatchBusy, setFavMatchBusy] = useState(false);
  // Match-Coach: per-favorite AI tip explaining which skill/experience
  // the user can strengthen to raise the match score. Cached server-side
  // on `job_favorites.coach_tip`; loaded busy-state per external_id.
  const [coachTips, setCoachTips] = useState({}); // { [external_id]: { focus_skills, advice, generated_at } }
  const [coachBusy, setCoachBusy] = useState({}); // { [external_id]: true }

  // Weekly Match Radar — persisted preference that mails the user
  // the top matches every Monday. Toggle syncs with the backend.
  const [radar, setRadar] = useState(null); // {enabled, query, location, threshold, last_sent}
  const [radarBusy, setRadarBusy] = useState(false);

  useEffect(() => {
    api.get("/preferences/match-radar").then((r) => setRadar(r.data || null)).catch(() => {});
  }, []);

  // Persist filter state (query + location + selected categories/emp-types)
  // to localStorage so returning users don't lose their picks.
  useEffect(() => {
    try {
      localStorage.setItem("jg_jobsearch_filters_v1", JSON.stringify({
        q, location,
        categories: selectedCategories,
        employmentTypes: selectedEmploymentTypes,
      }));
    } catch { /* quota exceeded — silently drop */ }
  }, [q, location, selectedCategories, selectedEmploymentTypes]);

  // Persist last fetched results (dedupe'd list, source-mix + AI match
  // scores) so switching dashboard-tabs doesn't wipe the search state.
  // Cleared on refresh > 6h old (see `savedResults` initialiser above).
  useEffect(() => {
    try {
      if (results === null) {
        localStorage.removeItem("jg_jobsearch_results_v1");
      } else {
        // Cap payload so we don't blow past the 5MB localStorage quota when
        // a user does many searches with long descriptions.
        const trimmed = (results || []).slice(0, 60).map((j) => ({
          ...j,
          description: (j.description || "").slice(0, 800),
        }));
        localStorage.setItem("jg_jobsearch_results_v1", JSON.stringify({
          saved_at: new Date().toISOString(),
          results: trimmed,
          sources,
          matches,
        }));
      }
    } catch { /* quota exceeded — silently drop */ }
  }, [results, sources, matches]);

  // Handle the unsubscribe deep-link from the weekly email
  // (`/dashboard?view=jobsearch&mr=off`) — one-shot disable.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("mr") === "off") {
      api.put("/preferences/match-radar", {
        enabled: false, query: "x", location: "Nederland", category: "", threshold: 70,
      }).then(() => {
        setRadar((prev) => ({ ...(prev || {}), enabled: false }));
        toast.success(t("jsRadarUnsubDone"));
        // Clean the URL so a page refresh doesn't fire again.
        const url = new URL(window.location.href);
        url.searchParams.delete("mr");
        window.history.replaceState({}, "", url.toString());
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/jobs-search/categories").then((r) => {
      setCategories(r.data?.categories || []);
    }).catch(() => {});
  }, []);

  // Load favorites once on mount so the icon-state matches server truth.
  useEffect(() => {
    api.get("/jobs-search/favorites").then((r) => {
      const list = r.data?.favorites || [];
      setFavorites(list);
      setFavoriteIds(new Set(list.map((j) => j.external_id)));
      // Hydrate persisted match scores so the badge appears immediately
      // when the user opens the favorites tab.
      const persisted = {};
      const persistedCoach = {};
      for (const j of list) {
        if (typeof j.match_score === "number") {
          persisted[j.external_id] = { score: j.match_score, reason: j.match_reason || "" };
        }
        if (j.coach_tip && (j.coach_tip.focus_skills?.length || j.coach_tip.advice)) {
          persistedCoach[j.external_id] = j.coach_tip;
        }
      }
      setFavMatches(persisted);
      setCoachTips(persistedCoach);
    }).catch(() => {});
  }, []);

  const toggleFavorite = async (job) => {
    const extId = job.external_id || job.url;
    if (!extId) return;
    const isFav = favoriteIds.has(extId);
    // Optimistic update — snappier UX than waiting on the round-trip.
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(extId); else next.add(extId);
      return next;
    });
    try {
      if (isFav) {
        await api.delete(`/jobs-search/favorites/${encodeURIComponent(extId)}`);
        setFavorites((prev) => prev.filter((f) => f.external_id !== extId));
        toast.success(t("jsFavRemoved"));
      } else {
        await api.post("/jobs-search/favorites", job);
        setFavorites((prev) => [{ ...job, external_id: extId, saved_at: new Date().toISOString() }, ...prev]);
        toast.success(t("jsFavAdded"));
      }
    } catch (err) {
      // Rollback optimistic update.
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(extId); else next.delete(extId);
        return next;
      });
      toast.error(err?.response?.data?.detail || t("jsFavError"));
    }
  };

  /**
   * "Niet voor mij" — flag a vacancy as user-hidden so it disappears from
   * the current view AND from future search runs (backend filters it out
   * server-side, plus removes any matching favorite doc). Optimistic UI
   * with a toast-based undo so a mis-click is trivially recoverable.
   * Optional `reason` captures why the user dismissed — future match
   * scoring will lean on these hints.
   */
  const hideJob = async (job, reason = "") => {
    const extId = job.external_id || job.url;
    if (!extId) return;
    // Optimistic: remove from BOTH results and favorites lists so the
    // click feels instant. Rollback on failure.
    // `results` is null before the user has run any search — guard the
    // filter call so hiding a favorite from the favorites tab (where
    // no search has run yet) doesn't crash the component.
    const prevResults = results;
    const prevFavorites = favorites;
    const prevFavIds = favoriteIds;
    setResults((rs) => rs ? rs.filter((r) => (r.external_id || r.url) !== extId) : rs);
    setFavorites((fs) => (fs || []).filter((f) => f.external_id !== extId));
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      next.delete(extId);
      return next;
    });
    try {
      await api.post("/jobs-search/hide", {
        external_id: extId,
        title: job.title || "",
        company: job.company || "",
        url: job.url || "",
        source: job.source || "",
        reason: (reason || "").trim(),
      });
      toast.success(t("jsHideDone"), {
        action: {
          label: t("jsHideUndo"),
          onClick: async () => {
            try {
              await api.delete(`/jobs-search/hide/${encodeURIComponent(extId)}`);
              // Re-hydrate only the list the card came from. Simplest
              // safe restore: put back in results if we still have context.
              // Guard against a null results list — the user might have
              // dismissed from the favorites tab before running any search.
              setResults((rs) => {
                if (!rs) return rs;
                if (rs.some((r) => (r.external_id || r.url) === extId)) return rs;
                return [job, ...rs];
              });
              toast.success(t("jsHideUndone"));
            } catch (e) {
              toast.error(t("jsHideUndoFail"));
            }
          },
        },
      });
    } catch (err) {
      // Rollback all three optimistic updates.
      setResults(prevResults);
      setFavorites(prevFavorites);
      setFavoriteIds(prevFavIds);
      toast.error(err?.response?.data?.detail || t("jsHideFail"));
    }
  };

  const search = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!q.trim() || q.trim().length < 2) return;
    setBusy(true);
    setMatches(null); // stale after a new search
    try {
      // Fan-out: build cartesian product of selected filters. Empty arrays
      // fall back to a single "no filter" call. Runs in parallel and merges
      // results dedup'd by external_id so the user perceives one query.
      const cats = selectedCategories.length ? selectedCategories : [""];
      const emps = selectedEmploymentTypes.length ? selectedEmploymentTypes : [""];
      const combos = [];
      for (const c of cats) for (const e2 of emps) combos.push({ c, e2 });
      const responses = await Promise.all(combos.map((combo) => api.get("/jobs-search", {
        params: {
          q: q.trim(),
          location: location.trim() || "Nederland",
          ...(combo.c ? { category: combo.c } : {}),
          ...(combo.e2 ? { employment_type: combo.e2 } : {}),
          ...(bypassSkips.size > 0 ? { skip_bypass: [...bypassSkips].join(",") } : {}),
        },
      }).then((r) => r.data).catch(() => ({ results: [], sources: null }))));
      // Merge, dedupe by external_id (fall back to url), preserve order.
      const seen = new Set();
      const merged = [];
      const totalSources = { jsearch: 0, adzuna: 0 };
      let totalSkipped = 0;
      const skipItems = [];
      for (const data of responses) {
        for (const job of (data?.results || [])) {
          const key = job.external_id || job.url || `${job.title}|${job.company}`;
          if (seen.has(key)) continue;
          seen.add(key);
          merged.push(job);
        }
        if (data?.sources) {
          totalSources.jsearch += data.sources.jsearch || 0;
          totalSources.adzuna += data.sources.adzuna || 0;
        }
        if (typeof data?.skipped === "number") totalSkipped += data.skipped;
        if (Array.isArray(data?.skipped_items)) skipItems.push(...data.skipped_items);
      }
      setResults(merged);
      setSources(totalSources);
      setSkippedCount(totalSkipped);
      setSkippedItems(skipItems.slice(0, 20));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("jsSearchFailed"));
      setResults([]);
    } finally {
      setBusy(false);
    }
  }, [q, location, selectedCategories, selectedEmploymentTypes, t, bypassSkips]);

  // Re-run search automatically when a filter chip toggles (only if we've
  // already searched — no accidental first search).
  useEffect(() => {
    if (results !== null) search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories, selectedEmploymentTypes]);

  const toggleCategory = (tag) => setSelectedCategories((prev) => prev.includes(tag) ? prev.filter((t2) => t2 !== tag) : [...prev, tag]);
  const toggleEmploymentType = (val) => setSelectedEmploymentTypes((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);

  const formatApiError = (err, fallback) => {
    const d = err?.response?.data?.detail;
    if (Array.isArray(d)) return d.map((e) => e?.msg || String(e)).join("; ");
    if (typeof d === "string") return d;
    return fallback;
  };

  const saveJob = async (job, status = "saved") => {
    const payload = {
      url: job.url || "",
      job_title: job.title || "",
      company: job.company || "",
      location: job.location || "",
      description: (job.description || "").replace(/<[^>]+>/g, ""),
      status,
      channel: "linkedin",
      job_type: "incompany",
      date_applied: new Date().toISOString().slice(0, 10),
    };
    const { data } = await api.post("/jobs", payload);
    return data;
  };

  const addToTracker = async (job, i) => {
    setRowBusy(`add-${i}`);
    try {
      // Default "saved" — user just parked the job for follow-up but has
      // not applied yet. Prevents the tracker from falsely counting it
      // toward the "applied" funnel until the user actively sends off a
      // letter or flips the status manually.
      await saveJob(job, "saved");
      setAddedIds((prev) => new Set([...prev, i]));
      toast.success(t("jsAddedToTracker", { title: job.title }));
      onJobCreated?.();
    } catch (err) {
      toast.error(formatApiError(err, t("jsAddFailed")));
    } finally {
      setRowBusy(null);
    }
  };

  const addAndWriteLetter = async (job, i) => {
    setRowBusy(`letter-${i}`);
    try {
      // Save as "saved" (not "applied") — we only promote to "applied"
      // AFTER the letter actually gets generated, so that abandoning the
      // wizard leaves the vacancy in the overview as a normal saved
      // item instead of a phantom "sollicitatie zonder brief".
      // The letter-generate endpoint handles the status bump upstream.
      const saved = await saveJob(job, "saved");
      setAddedIds((prev) => new Set([...prev, i]));
      toast.success(t("jsAddedToTracker", { title: job.title }));
      onJobCreated?.();
      onCreatedForLetter?.(saved);
    } catch (err) {
      toast.error(formatApiError(err, t("jsAddFailed")));
    } finally {
      setRowBusy(null);
    }
  };

  const runCvMatch = async () => {
    if (!results || results.length === 0) return;
    setMatchBusy(true);
    try {
      const r = await api.post("/jobs-search/match", { jobs: results });
      setMatches(r.data?.matches || {});
      toast.success(t("jsMatchDone", { n: Object.keys(r.data?.matches || {}).length }));
    } catch (err) {
      toast.error(formatApiError(err, t("jsMatchFailed")));
    } finally {
      setMatchBusy(false);
    }
  };

  const runFavoritesMatch = async () => {
    if (favorites.length === 0) return;
    setFavMatchBusy(true);
    try {
      const r = await api.post("/jobs-search/favorites/match");
      const next = r.data?.matches || {};
      setFavMatches(next);
      toast.success(t("jsMatchDone", { n: Object.keys(next).length }));
    } catch (err) {
      toast.error(formatApiError(err, t("jsMatchFailed")));
    } finally {
      setFavMatchBusy(false);
    }
  };

  const runCoachTip = async (extId) => {
    if (!extId) return;
    setCoachBusy((prev) => ({ ...prev, [extId]: true }));
    try {
      const r = await api.post(`/jobs-search/favorites/${encodeURIComponent(extId)}/coach-tip`);
      const tip = r.data?.coach_tip;
      if (tip) {
        setCoachTips((prev) => ({ ...prev, [extId]: tip }));
      }
    } catch (err) {
      toast.error(formatApiError(err, t("jsCoachFailed")));
    } finally {
      setCoachBusy((prev) => {
        const next = { ...prev };
        delete next[extId];
        return next;
      });
    }
  };

  // Auto cue-cards for the vacancy-detail modal. Called every time the
  // detail opens for a favorited job; the backend caches the 3-line
  // spiek-script on the favorite doc so repeat opens are instant.
  const loadCueCards = useCallback(async (extId) => {
    if (!extId || cueCards[extId] || cueCardsBusy[extId]) return;
    setCueCardsBusy((p) => ({ ...p, [extId]: true }));
    try {
      const r = await api.get(`/jobs-search/favorites/${encodeURIComponent(extId)}/cue-cards`);
      if (Array.isArray(r.data?.cue_cards)) {
        setCueCards((p) => ({ ...p, [extId]: r.data.cue_cards }));
      }
    } catch {
      // Silent — feature is a nice-to-have; a failed load just hides
      // the block. User can still click "Video-pitch" and get the full
      // script from the recorder modal.
    } finally {
      setCueCardsBusy((p) => {
        const n = { ...p };
        delete n[extId];
        return n;
      });
    }
  }, [cueCards, cueCardsBusy]);

  // Trigger cue-card load whenever the detail modal opens for a favorited job.
  useEffect(() => {
    if (!detailJob) return;
    const extId = detailJob.external_id || detailJob.url;
    if (extId && favoriteIds.has(extId)) {
      loadCueCards(extId);
    }
  }, [detailJob, favoriteIds, loadCueCards]);

  const toggleRadar = async () => {
    // Turning ON without a saved query is nonsensical — save the current
    // search state as the radar target. Turning OFF just flips the flag.
    if (!radar) return;
    const nextEnabled = !radar.enabled;
    if (nextEnabled && (!q.trim() || q.trim().length < 2)) {
      toast.error(t("jsRadarNeedQuery"));
      return;
    }
    setRadarBusy(true);
    try {
      const payload = nextEnabled
        ? {
            enabled: true,
            query: q.trim(),
            location: location.trim() || "Nederland",
            categories: selectedCategories,
            employment_types: selectedEmploymentTypes,
            threshold: matchThreshold,
          }
        : {
            enabled: false,
            query: radar.query || q.trim() || "x",
            location: radar.location || "Nederland",
            categories: radar.categories || [],
            employment_types: radar.employment_types || [],
            threshold: radar.threshold || 70,
          };
      await api.put("/preferences/match-radar", payload);
      setRadar((prev) => ({ ...(prev || {}), ...payload }));
      toast.success(nextEnabled ? t("jsRadarOn") : t("jsRadarOff"));
    } catch (err) {
      toast.error(formatApiError(err, t("jsRadarSaveFailed")));
    } finally {
      setRadarBusy(false);
    }
  };

  // Match-based ordering — surfaces the strongest matches first when
  // the user picks "high" (default power move) or lifts the least-fit
  // to top when picking "low". `none` preserves the backend order which
  // is dedup-then-source-interleave.
  const [matchSort, setMatchSort] = useState("none");

  // Apply the match filter + optional match% sort. Sort is stable-ish —
  // items without match scores stay at the bottom regardless of direction
  // so unscored results don't leapfrog scored ones by accident.
  const visibleResults = useMemo(() => {
    if (!results) return [];
    const base = !matches
      ? results.map((j, i) => ({ ...j, __idx: i }))
      : results
        .map((j, i) => ({ ...j, __idx: i, __match: matches[i] }))
        .filter((j) => !j.__match || j.__match.score >= matchThreshold);
    if (matchSort === "none") return base;
    const scored = base.filter((j) => typeof j.__match?.score === "number");
    const unscored = base.filter((j) => typeof j.__match?.score !== "number");
    scored.sort((a, b) =>
      matchSort === "high"
        ? b.__match.score - a.__match.score
        : a.__match.score - b.__match.score
    );
    return [...scored, ...unscored];
  }, [results, matches, matchThreshold, matchSort]);

  const fmtSalary = (min, max, cur) => {
    if (!min && !max) return null;
    const s = cur === "EUR" ? "€" : (cur || "");
    if (min && max) return `${s}${min.toLocaleString("nl-NL")} - ${s}${max.toLocaleString("nl-NL")}`;
    return `${s}${(min || max).toLocaleString("nl-NL")}`;
  };

  const matchColor = (score) => {
    if (score >= 75) return { bg: "#065F46", label: "text-emerald-700 bg-emerald-50 border-emerald-200" };
    if (score >= 50) return { bg: "#B45309", label: "text-amber-700 bg-amber-50 border-amber-200" };
    return { bg: "#991B1B", label: "text-red-700 bg-red-50 border-red-200" };
  };

  const filteredOutCount = results && matches
    ? results.length - visibleResults.length
    : 0;

  return (
    <div className="space-y-6" data-testid="jobsearch-panel">
      <div>
        <p className="ed-eyebrow">{t("jsEyebrow")}</p>
        <h2 className="font-serif text-3xl mt-2 leading-tight">{t("jsTitle")}</h2>
        <p className="text-sm text-neutral-600 mt-2 max-w-2xl leading-relaxed">{t("jsBody")}</p>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">
            {t("jsSources")}:
          </span>
          {PROMOTED_SOURCES.map((s) => (
            <span
              key={s.key}
              data-testid={`source-legend-${s.key}`}
              className="inline-flex items-center gap-1 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-white rounded-full px-2 py-0.5"
              style={{ backgroundColor: s.color }}
            >
              {s.label}
            </span>
          ))}
        </div>
      </div>

      {/* Tab switcher — Zoeken vs Favorieten vs Geschiedenis.
          Bumped from thin underline-tabs to a chunky filled-segment design
          because users kept scrolling past it. Active tab = solid dark
          rectangle with light text; inactive = outlined and hover-inverts. */}
      <div
        className="bg-neutral-100 border border-neutral-300 p-1 inline-flex items-center gap-1"
        data-testid="jobsearch-tabs"
      >
        <button
          type="button"
          data-testid="jobsearch-tab-search"
          onClick={() => setActiveTab("search")}
          className={`px-5 py-2.5 font-mono text-[0.72rem] uppercase tracking-[0.15em] border inline-flex items-center gap-2 transition-colors ${
            activeTab === "search"
              ? "bg-neutral-900 border-neutral-900 text-[#F9F8F6] shadow-sm"
              : "bg-transparent border-transparent text-neutral-600 hover:bg-white hover:border-neutral-400 hover:text-neutral-900"
          }`}
        >
          <Search size={13} strokeWidth={1.75} />
          {t("jsTabSearch")}
        </button>
        <button
          type="button"
          data-testid="jobsearch-tab-favorites"
          onClick={() => setActiveTab("favorites")}
          className={`px-5 py-2.5 font-mono text-[0.72rem] uppercase tracking-[0.15em] border inline-flex items-center gap-2 transition-colors ${
            activeTab === "favorites"
              ? "bg-rose-600 border-rose-600 text-white shadow-sm"
              : "bg-transparent border-transparent text-neutral-600 hover:bg-white hover:border-neutral-400 hover:text-neutral-900"
          }`}
        >
          <Heart size={13} strokeWidth={1.75} fill={activeTab === "favorites" ? "currentColor" : "none"} />
          {t("jsTabFavorites")}
          {favorites.length > 0 && (
            <span
              className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[0.62rem] font-semibold ${
                activeTab === "favorites"
                  ? "bg-white text-rose-700"
                  : "bg-rose-100 text-rose-700 border border-rose-300"
              }`}
              data-testid="jobsearch-tab-favorites-count"
            >
              {favorites.length}
            </span>
          )}
        </button>
        <button
          type="button"
          data-testid="jobsearch-tab-history"
          onClick={() => setActiveTab("history")}
          className={`px-5 py-2.5 font-mono text-[0.72rem] uppercase tracking-[0.15em] border inline-flex items-center gap-2 transition-colors ${
            activeTab === "history"
              ? "bg-neutral-900 border-neutral-900 text-[#F9F8F6] shadow-sm"
              : "bg-transparent border-transparent text-neutral-600 hover:bg-white hover:border-neutral-400 hover:text-neutral-900"
          }`}
        >
          <FilterX size={13} strokeWidth={1.75} />
          {t("jsTabHistory")}
        </button>
      </div>

      {/* Branche chips — shown ABOVE the query/location form so users
          intuitively narrow the field before clicking Zoeken. */}
      {activeTab === "search" && categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="jobsearch-branches">
          <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mr-2">
            {t("jsBranches")}:
          </span>
          <button
            type="button"
            data-testid="branche-chip-all"
            onClick={() => setSelectedCategories([])}
            className={`font-mono text-[0.65rem] uppercase tracking-[0.12em] px-2.5 py-1 border transition-colors ${selectedCategories.length === 0 ? "bg-neutral-900 text-[#F9F8F6] border-neutral-900" : "border-neutral-300 hover:border-neutral-900"}`}
          >
            {t("jsBrancheAll")}
          </button>
          {categories.map((c) => {
            const active = selectedCategories.includes(c.tag);
            return (
              <button
                key={c.tag}
                type="button"
                data-testid={`branche-chip-${c.tag}`}
                onClick={() => toggleCategory(c.tag)}
                aria-pressed={active}
                className={`font-mono text-[0.65rem] uppercase tracking-[0.12em] px-2.5 py-1 border transition-colors inline-flex items-center gap-1 ${active ? "bg-neutral-900 text-[#F9F8F6] border-neutral-900" : "border-neutral-300 hover:border-neutral-900"}`}
              >
                {active && <Check size={10} strokeWidth={2} />} {c.label}
              </button>
            );
          })}
          {selectedCategories.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedCategories([])}
              className="text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1 text-xs"
              data-testid="branche-chip-clear"
            >
              <X size={11} strokeWidth={1.75} /> {t("jsBrancheClear")}
            </button>
          )}
        </div>
      )}

      {/* Employment-type chips (Dienstverband) — multi-select */}
      {activeTab === "search" && (
      <div className="flex flex-wrap items-center gap-2" data-testid="jobsearch-employment-types">
        <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mr-2">
          {t("jsEmployment")}:
        </span>
        <button
          type="button"
          data-testid="employment-chip-all"
          onClick={() => setSelectedEmploymentTypes([])}
          className={`font-mono text-[0.65rem] uppercase tracking-[0.12em] px-2.5 py-1 border transition-colors ${selectedEmploymentTypes.length === 0 ? "bg-neutral-900 text-[#F9F8F6] border-neutral-900" : "border-neutral-300 hover:border-neutral-900"}`}
        >
          {t("jsEmploymentAll")}
        </button>
        {[
          { value: "FULLTIME", labelKey: "jsEmploymentFulltime", testId: "fulltime" },
          { value: "PARTTIME", labelKey: "jsEmploymentParttime", testId: "parttime" },
          { value: "CONTRACTOR", labelKey: "jsEmploymentContract", testId: "contract" },
          { value: "INTERN", labelKey: "jsEmploymentIntern", testId: "intern" },
        ].map((opt) => {
          const active = selectedEmploymentTypes.includes(opt.value);
          return (
            <button
              key={opt.testId}
              type="button"
              data-testid={`employment-chip-${opt.testId}`}
              onClick={() => toggleEmploymentType(opt.value)}
              aria-pressed={active}
              className={`font-mono text-[0.65rem] uppercase tracking-[0.12em] px-2.5 py-1 border transition-colors inline-flex items-center gap-1 ${active ? "bg-neutral-900 text-[#F9F8F6] border-neutral-900" : "border-neutral-300 hover:border-neutral-900"}`}
            >
              {active && <Check size={10} strokeWidth={2} />} {t(opt.labelKey)}
            </button>
          );
        })}
        {selectedEmploymentTypes.length > 0 && (
          <button
            type="button"
            onClick={() => setSelectedEmploymentTypes([])}
            className="text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1 text-xs"
            data-testid="employment-chip-clear"
          >
            <X size={11} strokeWidth={1.75} /> {t("jsEmploymentClear")}
          </button>
        )}
      </div>
      )}

      {/* Search form — placed AFTER branche + dienstverband so users choose
          filters first, then type their role and click Zoeken. */}
      {activeTab === "search" && (
      <form onSubmit={search} className="ed-card p-5 grid grid-cols-1 md:grid-cols-[2fr_1fr_auto] gap-3" data-testid="jobsearch-form">
        <label className="block">
          <span className="ed-label mb-1 block">{t("jsFieldQ")}</span>
          <input
            type="text"
            data-testid="jobsearch-input-q"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="React developer, marketing manager, …"
            className="w-full border border-neutral-300 px-3 py-2 text-sm focus:border-neutral-900 outline-none"
          />
        </label>
        <label className="block">
          <span className="ed-label mb-1 block">{t("jsFieldLocation")}</span>
          <input
            type="text"
            data-testid="jobsearch-input-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Amsterdam"
            className="w-full border border-neutral-300 px-3 py-2 text-sm focus:border-neutral-900 outline-none"
          />
        </label>
        <button
          type="submit"
          data-testid="jobsearch-submit"
          disabled={busy || q.trim().length < 2}
          className="ed-btn self-end inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Search size={13} strokeWidth={1.75} />
          {busy ? "…" : t("jsSearch")}
        </button>
      </form>
      )}

      {/* Skip-keywords blocklist — collapsible bar directly under the search
          form. Vacatures die één van deze woorden in titel/beschrijving
          bevatten worden server-side gefilterd. Toont een badge met het
          aantal verborgen resultaten van de laatste zoekopdracht. */}
      {activeTab === "search" && (
        <div className="ed-card p-4" data-testid="jobsearch-skip">
          <button
            type="button"
            data-testid="jobsearch-skip-toggle"
            onClick={() => setSkipOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3 text-left"
            aria-expanded={skipOpen}
          >
            <span className="ed-eyebrow inline-flex items-center gap-2">
              <FilterX size={11} strokeWidth={1.6} /> {t("jsSkipTitle")}
              {skipKeywords.length > 0 && (
                <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500 normal-case">
                  · {t("jsSkipCount", { n: skipKeywords.length })}
                </span>
              )}
              {bypassSkips.size > 0 && (
                <span
                  data-testid="jobsearch-bypass-badge"
                  className="font-mono text-[0.6rem] uppercase tracking-[0.12em] px-2 py-0.5 border border-amber-400 bg-amber-50 text-amber-900 normal-case inline-flex items-center gap-1.5"
                  title={[...bypassSkips].join(", ")}
                >
                  {t("jsBypassBadge", { n: bypassSkips.size })}
                  <button
                    type="button"
                    data-testid="jobsearch-bypass-clear"
                    onClick={(e) => {
                      e.stopPropagation();
                      setBypassSkips(new Set());
                      toast.success(t("jsBypassRestored"));
                    }}
                    className="hover:text-amber-700"
                    aria-label={t("jsBypassClear")}
                  >
                    <X size={10} strokeWidth={2} />
                  </button>
                </span>
              )}
              {skippedCount > 0 && (
                <span
                  data-testid="jobsearch-skip-badge"
                  className="relative inline-block"
                  onMouseEnter={() => setSkipTipOpen(true)}
                  onMouseLeave={() => setSkipTipOpen(false)}
                  onFocus={() => setSkipTipOpen(true)}
                  onBlur={() => setSkipTipOpen(false)}
                >
                  <span
                    tabIndex={0}
                    className="font-mono text-[0.6rem] uppercase tracking-[0.12em] px-2 py-0.5 border border-rose-300 bg-rose-50 text-rose-800 normal-case cursor-help"
                  >
                    {t("jsSkippedBadge", { n: skippedCount })}
                  </span>
                  {skipTipOpen && skippedItems.length > 0 && (
                    <div
                      data-testid="jobsearch-skip-badge-tooltip"
                      role="tooltip"
                      className="absolute z-30 left-0 top-full mt-1 min-w-[22rem] max-w-md ed-card p-3 space-y-2 shadow-lg text-left"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <p className="ed-eyebrow">{t("jsSkipReasonTitle")}</p>
                      <ul className="space-y-1.5 max-h-64 overflow-y-auto">
                        {skippedItems.map((it, idx) => (
                          <li key={idx} className="text-xs text-neutral-700 leading-snug" data-testid={`jobsearch-skip-reason-${idx}`}>
                            <span className="font-serif text-sm text-neutral-900 block">
                              {it.title || "—"}{it.company ? ` · ${it.company}` : ""}
                            </span>
                            <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-rose-700">
                              {t("jsSkipReasonMatched")} {(it.matched || []).map((m) => `"${m}"`).join(", ")}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </span>
              )}
            </span>
            <span className="font-mono text-xs text-neutral-500">{skipOpen ? "▲" : "▼"}</span>
          </button>
          {skipOpen && (
            <div className="mt-3 space-y-3" data-testid="jobsearch-skip-body">
              <p className="text-xs text-neutral-600 leading-relaxed max-w-2xl">{t("jsSkipHint")}</p>
              {skipKeywords.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="jobsearch-skip-list">
                  {skipKeywords.map((kw) => (
                    <span
                      key={kw}
                      data-testid={`jobsearch-skip-pill-${kw}`}
                      className="inline-flex items-center gap-2 border border-rose-300 bg-rose-50 text-rose-900 font-mono text-[0.65rem] uppercase tracking-[0.12em] px-3 py-1.5"
                    >
                      {kw}
                      <button
                        type="button"
                        data-testid={`jobsearch-skip-remove-${kw}`}
                        onClick={() => removeSkipKeyword(kw)}
                        className="text-rose-500 hover:text-rose-900"
                        aria-label={t("jsSkipRemove")}
                      >×</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  data-testid="jobsearch-skip-input"
                  value={skipInput}
                  onChange={(e) => setSkipInput(e.target.value.slice(0, 60))}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkipKeyword(); } }}
                  placeholder={t("jsSkipPlaceholder")}
                  className="flex-1 border border-neutral-300 px-3 py-2 text-sm focus:border-neutral-900 outline-none"
                  maxLength={60}
                />
                <button
                  type="button"
                  data-testid="jobsearch-skip-add"
                  onClick={addSkipKeyword}
                  disabled={skipSaving || !skipInput.trim() || skipKeywords.length >= 30}
                  className="ed-btn ed-btn-outline"
                >
                  {skipSaving ? "…" : t("jsSkipAdd")}
                </button>
              </div>

              {/* Smart suggestions — two rows, only rendered when there's
                  something to add. Clicking a chip promotes it to the
                  active blocklist and immediately re-runs the query. */}
              {(skipSug.from_insights.length > 0 || skipSug.from_cv_gaps.length > 0 || skipSugLoading) && (
                <div className="pt-3 mt-1 border-t border-neutral-200 space-y-3" data-testid="jobsearch-skip-suggestions">
                  {skipSug.from_insights.length > 0 && (
                    <div>
                      <p className="ed-eyebrow inline-flex items-center gap-2 mb-2">
                        <Sparkles size={11} strokeWidth={1.6} className="text-amber-600" />
                        {t("jsSkipSugInsightsTitle")}
                        <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500 normal-case">
                          · {t("jsSkipSugInsightsHint")}
                        </span>
                      </p>
                      <div className="flex flex-wrap gap-2" data-testid="jobsearch-skip-sug-insights">
                        {skipSug.from_insights.map((s) => (
                          <span
                            key={s}
                            className="inline-flex items-stretch border border-neutral-300 hover:border-neutral-900 transition-colors overflow-hidden group"
                          >
                            <button
                              type="button"
                              data-testid={`jobsearch-skip-sug-insight-${s}`}
                              onClick={() => saveSkipKeywords([...skipKeywords, s].slice(0, 30))}
                              disabled={skipSaving}
                              className="inline-flex items-center gap-1.5 font-mono text-[0.65rem] uppercase tracking-[0.12em] px-3 py-1.5 hover:bg-neutral-900 group-hover:bg-neutral-900 hover:text-[#F9F8F6] group-hover:text-[#F9F8F6] transition-colors"
                            >
                              <Plus size={11} strokeWidth={1.7} /> {s}
                            </button>
                            <button
                              type="button"
                              data-testid={`jobsearch-skip-sug-insight-dismiss-${s}`}
                              onClick={() => dismissSuggestion(s)}
                              disabled={skipSaving}
                              title={t("jsSkipSugDismiss")}
                              className="px-2 border-l border-neutral-300 group-hover:border-neutral-700 text-neutral-500 hover:text-rose-600 group-hover:text-neutral-400 hover:bg-neutral-100 group-hover:hover:bg-rose-900/40 transition-colors"
                              aria-label={t("jsSkipSugDismiss")}
                            >
                              <X size={11} strokeWidth={1.7} />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {skipSug.from_cv_gaps.length > 0 && (
                    <div>
                      <p className="ed-eyebrow inline-flex items-center gap-2 mb-2">
                        <FilterX size={11} strokeWidth={1.6} className="text-neutral-700" />
                        {t("jsSkipSugCvTitle")}
                        <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500 normal-case">
                          · {t("jsSkipSugCvHint")}
                        </span>
                      </p>
                      <div className="flex flex-wrap gap-2" data-testid="jobsearch-skip-sug-cv">
                        {skipSug.from_cv_gaps.map((s) => (
                          <span
                            key={s}
                            className="inline-flex items-stretch border border-neutral-300 hover:border-neutral-900 transition-colors overflow-hidden group"
                          >
                            <button
                              type="button"
                              data-testid={`jobsearch-skip-sug-cv-${s}`}
                              onClick={() => saveSkipKeywords([...skipKeywords, s].slice(0, 30))}
                              disabled={skipSaving}
                              className="inline-flex items-center gap-1.5 font-mono text-[0.65rem] uppercase tracking-[0.12em] px-3 py-1.5 hover:bg-neutral-900 group-hover:bg-neutral-900 hover:text-[#F9F8F6] group-hover:text-[#F9F8F6] transition-colors"
                            >
                              <Plus size={11} strokeWidth={1.7} /> {s}
                            </button>
                            <button
                              type="button"
                              data-testid={`jobsearch-skip-sug-cv-dismiss-${s}`}
                              onClick={() => dismissSuggestion(s)}
                              disabled={skipSaving}
                              title={t("jsSkipSugDismiss")}
                              className="px-2 border-l border-neutral-300 group-hover:border-neutral-700 text-neutral-500 hover:text-rose-600 group-hover:text-neutral-400 hover:bg-neutral-100 group-hover:hover:bg-rose-900/40 transition-colors"
                              aria-label={t("jsSkipSugDismiss")}
                            >
                              <X size={11} strokeWidth={1.7} />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {skipSugLoading && skipSug.from_insights.length === 0 && skipSug.from_cv_gaps.length === 0 && (
                    <p className="font-mono text-[0.65rem] uppercase tracking-[0.12em] text-neutral-500">{t("jsSkipSugLoading")}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* CV match toolbar — only shown once we have results */}
      {activeTab === "search" && results !== null && results.length > 0 && (
        <div className="ed-card p-5 flex flex-wrap items-center gap-4" data-testid="jobsearch-match-toolbar">
          <div className="flex items-start gap-3 flex-1 min-w-[16rem]">
            <div className="w-9 h-9 rounded-full bg-neutral-900 text-[#F9F8F6] flex items-center justify-center shrink-0">
              <Target size={15} strokeWidth={1.5} />
            </div>
            <div>
              <p className="font-serif text-base leading-tight">{t("jsMatchTitle")}</p>
              <p className="text-xs text-neutral-600 mt-1 leading-relaxed max-w-md">{t("jsMatchBody")}</p>
            </div>
          </div>
          <button
            type="button"
            data-testid="jobsearch-match-run"
            onClick={runCvMatch}
            disabled={matchBusy}
            className="ed-btn inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Sparkles size={12} strokeWidth={1.75} />
            {matchBusy ? t("jsMatchRunning") : (matches ? t("jsMatchRerun") : t("jsMatchRun"))}
          </button>
          {matchBusy && (
            <ThinkingLoader stage="match" testId="jobsearch-match-thinking" />
          )}
          {matches && (
            <div className="flex items-center gap-3 border-l border-neutral-200 pl-4">
              <label className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.12em] text-neutral-600">
                <span data-testid="jobsearch-match-threshold-label">{t("jsMatchThreshold", { pct: matchThreshold })}</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={matchThreshold}
                  onChange={(e) => setMatchThreshold(Number(e.target.value))}
                  data-testid="jobsearch-match-threshold"
                  className="w-32 accent-neutral-900"
                />
              </label>
              {filteredOutCount > 0 && (
                <span className="text-xs text-neutral-500" data-testid="jobsearch-match-filtered">
                  {t("jsMatchFiltered", { n: filteredOutCount })}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Match Radar toggle card — persists to backend so scheduler
          picks it up every Monday morning. */}
      {activeTab === "search" && radar && (
        <>
          <MatchRadarInsights enabled={radar.enabled} />
          <div className="ed-card p-5" data-testid="jobsearch-radar-card">
          <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-colors ${radar.enabled ? "bg-emerald-700 text-[#F9F8F6]" : "bg-neutral-200 text-neutral-500"}`}>
                <Radar size={15} strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <p className="font-serif text-base leading-tight">{t("jsRadarTitle")}</p>
                <p className="text-xs text-neutral-600 mt-1 leading-relaxed">
                  {t("jsRadarBody")}
                </p>
                {radar.enabled && radar.query && (
                  <div className="mt-2 space-y-1" data-testid="jobsearch-radar-active-query">
                    <p className="text-xs text-emerald-800 font-mono uppercase tracking-[0.1em] inline-flex items-center gap-1">
                      <Check size={11} strokeWidth={2} /> {t("jsRadarActiveOn", { q: radar.query, loc: radar.location || "Nederland" })}
                    </p>
                    {((radar.categories && radar.categories.length) || (radar.employment_types && radar.employment_types.length)) && (
                      <p className="text-[0.65rem] text-neutral-500 font-mono uppercase tracking-[0.1em]">
                        {radar.categories?.length ? radar.categories.join(", ") : "—"}
                        {radar.employment_types?.length ? " · " + radar.employment_types.join(", ") : ""}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Compact pill toggle — smaller footprint stays comfortably
                inside the card, aligns to the title row via mt-0.5. */}
            <button
              type="button"
              role="switch"
              aria-checked={radar.enabled}
              data-testid="jobsearch-radar-toggle"
              onClick={toggleRadar}
              disabled={radarBusy}
              className={`relative w-10 h-6 rounded-full transition-colors shrink-0 mt-0.5 ${radar.enabled ? "bg-emerald-700" : "bg-neutral-300"} disabled:opacity-50`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${radar.enabled ? "translate-x-4" : "translate-x-0"}`}
              />
            </button>
          </div>
        </div>
        </>
      )}


      {/* Loading state — shown while an active search is in flight so the
          user knows aggregating across LinkedIn, Indeed and Adzuna can
          legitimately take 5-10 seconds. */}
      {activeTab === "search" && busy && (
        <div
          className="border border-dashed border-neutral-300 p-8 text-center flex flex-col items-center gap-3"
          data-testid="jobsearch-loading"
        >
          <div className="w-10 h-10 rounded-full border-2 border-neutral-200 border-t-neutral-900 animate-spin" aria-hidden />
          <p className="font-serif text-lg text-neutral-800">{t("jsLoadingTitle")}</p>
          <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 max-w-md">
            {t("jsLoadingHint")}
          </p>
        </div>
      )}


      {/* Results */}
      {activeTab === "search" && !busy && results !== null && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500" data-testid="jobsearch-results-count">
              {t("jsResults", { n: visibleResults.length })}
              {matches && ` / ${results.length}`}
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              {matches && (
                <div
                  className="inline-flex items-center border border-neutral-300"
                  data-testid="jobsearch-sort-group"
                  title={t("jsSortHint")}
                >
                  <span className="font-mono text-[0.55rem] uppercase tracking-[0.15em] text-neutral-500 px-2 border-r border-neutral-300 self-stretch flex items-center">
                    {t("jsSortLabel")}
                  </span>
                  <button
                    type="button"
                    data-testid="jobsearch-sort-none"
                    onClick={() => setMatchSort("none")}
                    className={`px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] transition-colors ${
                      matchSort === "none"
                        ? "bg-neutral-900 text-[#F9F8F6]"
                        : "hover:bg-neutral-100"
                    }`}
                  >
                    {t("jsSortNone")}
                  </button>
                  <button
                    type="button"
                    data-testid="jobsearch-sort-high"
                    onClick={() => setMatchSort("high")}
                    className={`px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] border-l border-neutral-300 transition-colors ${
                      matchSort === "high"
                        ? "bg-emerald-700 text-white"
                        : "hover:bg-neutral-100"
                    }`}
                  >
                    ↓ {t("jsSortHigh")}
                  </button>
                  <button
                    type="button"
                    data-testid="jobsearch-sort-low"
                    onClick={() => setMatchSort("low")}
                    className={`px-2.5 py-1 font-mono text-[0.6rem] uppercase tracking-[0.15em] border-l border-neutral-300 transition-colors ${
                      matchSort === "low"
                        ? "bg-amber-600 text-white"
                        : "hover:bg-neutral-100"
                    }`}
                  >
                    ↑ {t("jsSortLow")}
                  </button>
                </div>
              )}
              {sources && (
                <p className="font-mono text-[0.6rem] text-neutral-500">
                  LinkedIn/Indeed: {sources.jsearch} · Adzuna: {sources.adzuna}
                </p>
              )}
              <button
                type="button"
                data-testid="jobsearch-refresh"
                onClick={() => search()}
                disabled={busy || q.trim().length < 2}
                className="font-mono text-[0.6rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-default"
                title={t("jsRefreshHint")}
              >
                <RefreshCw size={11} strokeWidth={1.75} /> {t("jsRefresh")}
              </button>
            </div>
          </div>

          {visibleResults.length === 0 ? (
            <div className="border border-dashed border-neutral-300 p-8 text-center" data-testid="jobsearch-empty">
              <p className="text-sm text-neutral-600">
                {matches && results.length > 0 ? t("jsMatchNoneAboveThreshold") : t("jsEmptyTitle")}
              </p>
              <p className="font-mono text-[0.65rem] text-neutral-500 mt-2">{t("jsEmptyHint")}</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {visibleResults.map((job) => {
                const i = job.__idx;
                const salary = fmtSalary(job.salary_min, job.salary_max, job.salary_currency);
                const added = addedIds.has(i);
                const isBusy = rowBusy === `add-${i}` || rowBusy === `letter-${i}`;
                const match = job.__match;
                return (
                  <li
                    key={i}
                    data-testid={`jobsearch-result-${i}`}
                    className="ed-card p-5 cursor-pointer hover:border-neutral-500 transition-colors"
                    onClick={() => setDetailJob(job)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailJob(job); } }}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-serif text-lg leading-tight">{job.title}</p>
                          <JobSourceBadge job={job} />
                          {match && (
                            <span
                              data-testid={`match-badge-${i}`}
                              className="inline-flex items-center gap-1 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-white rounded-full px-2 py-0.5"
                              style={{ backgroundColor: matchColor(match.score).bg }}
                              title={match.reason}
                            >
                              {match.score}% match
                            </span>
                          )}
                          {job.category_label && !match && (
                            <span className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-neutral-500 border border-neutral-300 rounded-full px-2 py-0.5">
                              {job.category_label}
                            </span>
                          )}
                          {added && (
                            <span className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-emerald-700 border border-emerald-300 bg-emerald-50 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                              <Check size={10} strokeWidth={2} /> {t("jsAdded")}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-4 text-xs text-neutral-600">
                          {job.company && <span className="inline-flex items-center gap-1"><Building2 size={11} strokeWidth={1.5} /> {job.company}</span>}
                          {job.location && <span className="inline-flex items-center gap-1"><MapPin size={11} strokeWidth={1.5} /> {job.location}</span>}
                          {job.employment_type && <span className="inline-flex items-center gap-1"><Briefcase size={11} strokeWidth={1.5} /> {job.employment_type}</span>}
                          {salary && <span className="font-mono text-emerald-700">{salary}</span>}
                          {job.posted_at && <span className="inline-flex items-center gap-1"><Clock size={11} strokeWidth={1.5} /> {new Date(job.posted_at).toLocaleDateString("nl-NL")}</span>}
                        </div>
                        {match?.reason && (
                          <p className="mt-2 text-xs text-neutral-500 italic leading-relaxed" data-testid={`match-reason-${i}`}>
                            « {match.reason} »
                          </p>
                        )}
                        {job.description && (
                          <p className="mt-3 text-sm text-neutral-700 leading-relaxed" style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {job.description.replace(/<[^>]+>/g, "").slice(0, 300)}…
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 flex flex-col items-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <HideReasonPopover
                            job={job}
                            onHide={hideJob}
                            testId={`jobsearch-hide-${i}`}
                            className="p-2 border border-neutral-300 text-neutral-400 hover:border-neutral-900 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                            t={t}
                          />
                          <button
                            type="button"
                            data-testid={`jobsearch-fav-${i}`}
                            onClick={() => toggleFavorite(job)}
                            className={`p-2 border transition-colors ${favoriteIds.has(job.external_id || job.url) ? "border-rose-500 text-rose-600 bg-rose-50" : "border-neutral-300 text-neutral-400 hover:border-rose-500 hover:text-rose-600"}`}
                            title={favoriteIds.has(job.external_id || job.url) ? t("jsFavRemove") : t("jsFavAdd")}
                            aria-pressed={favoriteIds.has(job.external_id || job.url)}
                          >
                            <Heart size={13} strokeWidth={1.75} fill={favoriteIds.has(job.external_id || job.url) ? "currentColor" : "none"} />
                          </button>
                          <button
                            data-testid={`jobsearch-letter-${i}`}
                            onClick={() => addAndWriteLetter(job, i)}
                            disabled={added || isBusy}
                            className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 disabled:opacity-60 disabled:cursor-default px-3 py-2 inline-flex items-center justify-center gap-1.5"
                            title={t("jsAddAndLetterHint")}
                          >
                            <PenTool size={12} strokeWidth={1.75} /> {t("jsAddAndLetter")}
                          </button>
                        </div>
                        <span className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-neutral-400">
                          {t("jsClickForDetails")}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* Skip History tab — logs every hidden vacancy so users can review
          what was filtered out and (optionally) reopen it via the Skip
          Word Undo dialog. */}
      {activeTab === "history" && (
        <div data-testid="jobsearch-history-panel" className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="ed-eyebrow inline-flex items-center gap-2">
              <FilterX size={11} strokeWidth={1.6} /> {t("jsHistoryTitle")}
              {skipHistory && skipHistory.length > 0 && (
                <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500 normal-case">
                  · {t("jsHistoryCount", { n: skipHistory.length })}
                </span>
              )}
            </p>
            {skipHistory && skipHistory.length > 0 && (
              <button
                type="button"
                data-testid="jobsearch-history-clear"
                onClick={clearSkipHistory}
                disabled={skipHistoryBusy}
                className="ed-btn ed-btn-outline"
              >
                {t("jsHistoryClear")}
              </button>
            )}
          </div>
          {skipHistoryBusy && skipHistory === null && (
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.12em] text-neutral-500" data-testid="jobsearch-history-loading">{t("jsHistoryLoading")}</p>
          )}
          {skipHistory !== null && skipHistory.length === 0 && !skipHistoryBusy && (
            <div className="border border-dashed border-neutral-300 p-10 text-center" data-testid="jobsearch-history-empty">
              <FilterX size={22} strokeWidth={1.25} className="mx-auto text-neutral-400 mb-3" />
              <p className="font-serif text-lg text-neutral-800">{t("jsHistoryEmptyTitle")}</p>
              <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 mt-2 max-w-md mx-auto leading-relaxed">
                {t("jsHistoryEmptyHint")}
              </p>
            </div>
          )}
          {skipHistory !== null && skipHistory.length > 0 && (
            <div className="border border-neutral-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500 font-normal">{t("jsHistoryDate")}</th>
                    <th className="px-3 py-2 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500 font-normal">{t("jsHistoryQuery")}</th>
                    <th className="px-3 py-2 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500 font-normal">{t("jsHistoryJob")}</th>
                    <th className="px-3 py-2 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500 font-normal">{t("jsHistoryMatched")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200">
                  {skipHistory.map((row, i) => {
                    const matchedKws = (row.matched || []).filter((k) => typeof k === "string" && k.trim());
                    const canOpen = !!row.url;
                    const handleRowClick = () => {
                      if (!canOpen) return;
                      // If the row has a matched skip-keyword, offer the
                      // Restore dialog. If there's no matched info (legacy
                      // rows), fall back to a plain open.
                      if (matchedKws.length > 0) {
                        setRestoreDialog({ row, matched: matchedKws });
                      } else {
                        window.open(row.url, "_blank", "noopener,noreferrer");
                      }
                    };
                    return (
                      <tr
                        key={i}
                        data-testid={`jobsearch-history-row-${i}`}
                        className={`hover:bg-neutral-50 ${canOpen ? "cursor-pointer" : ""}`}
                        onClick={handleRowClick}
                        title={canOpen ? t("jsHistoryOpenTip") : ""}
                      >
                        <td className="px-3 py-2 font-mono text-[0.65rem] text-neutral-600 whitespace-nowrap">
                          {row.created_at ? new Date(row.created_at).toLocaleDateString(lang === "nl" ? "nl-NL" : lang === "ar" ? "ar" : "en-US", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </td>
                        <td className="px-3 py-2 font-mono text-[0.65rem] text-neutral-700">
                          {row.query || "—"}{row.location ? ` · ${row.location}` : ""}
                        </td>
                        <td className="px-3 py-2">
                          <span className="font-serif text-sm text-neutral-900 flex items-center gap-2">
                            {row.job_title || "—"}
                            {canOpen && (
                              <ExternalLink size={11} strokeWidth={1.6} className="text-neutral-400" data-testid={`jobsearch-history-row-open-${i}`} />
                            )}
                          </span>
                          {row.company && <span className="font-mono text-[0.6rem] uppercase tracking-[0.12em] text-neutral-500">{row.company}</span>}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {(row.matched || []).map((kw, j) => (
                              <span key={j} className="font-mono text-[0.6rem] uppercase tracking-[0.12em] px-2 py-0.5 border border-rose-300 bg-rose-50 text-rose-800">
                                {kw}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Favorites — no separate query controls, just a grid of saved cards. */}
      {activeTab === "favorites" && (
        <div data-testid="jobsearch-favorites-panel">
          {favorites.length === 0 ? (
            <div className="border border-dashed border-neutral-300 p-10 text-center" data-testid="jobsearch-favorites-empty">
              <Heart size={22} strokeWidth={1.25} className="mx-auto text-neutral-400 mb-3" />
              <p className="font-serif text-lg text-neutral-800">{t("jsFavEmptyTitle")}</p>
              <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 mt-2 max-w-md mx-auto leading-relaxed">
                {t("jsFavEmptyHint")}
              </p>
            </div>
          ) : (
            <>
              {/* CV match toolbar — mirrors the search tab's match card so
                  favorites can be scored + persisted. */}
              <div className="ed-card p-5 flex flex-wrap items-center gap-4 mb-4" data-testid="jobsearch-favorites-match-toolbar">
                <div className="flex items-center gap-3 flex-1 min-w-[220px]">
                  <div className="p-2 bg-neutral-100 rounded-full">
                    <Target size={16} strokeWidth={1.5} className="text-neutral-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-base leading-tight">{t("jsMatchTitle")}</p>
                    <p className="text-xs text-neutral-600 mt-1 leading-relaxed max-w-md">{t("jsFavMatchBody")}</p>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="jobsearch-favorites-match-run"
                  onClick={runFavoritesMatch}
                  disabled={favMatchBusy}
                  className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] border px-4 py-2.5 inline-flex items-center gap-2 transition-colors ${favMatchBusy ? "border-neutral-300 text-neutral-400 cursor-not-allowed" : "border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700"}`}
                >
                  <Sparkles size={12} strokeWidth={1.75} />
                  {favMatchBusy ? t("jsMatchRunning") : (Object.keys(favMatches).length ? t("jsFavMatchRerun") : t("jsFavMatchRun"))}
                </button>
                {favMatchBusy && (
                  <ThinkingLoader stage="match" testId="jobsearch-favorites-match-thinking" />
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <p className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500" data-testid="jobsearch-favorites-count">
                  {t("jsFavCount", { n: favorites.length })}
                </p>
              </div>
              <ul className="space-y-3">
                {favorites.map((job, i) => {
                  const favMatch = favMatches[job.external_id];
                  return (
                  <li
                    key={job.external_id}
                    data-testid={`jobsearch-favorite-${i}`}
                    className="ed-card p-5 cursor-pointer hover:border-neutral-500 transition-colors"
                    onClick={() => setDetailJob({ ...job, __match: favMatch })}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailJob({ ...job, __match: favMatch }); } }}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-serif text-lg leading-tight">{job.title}</p>
                          <JobSourceBadge job={job} />
                          {favMatch && (
                            <span
                              data-testid={`jobsearch-favorite-match-${i}`}
                              className="inline-flex items-center gap-1 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-white rounded-full px-2 py-0.5"
                              style={{ backgroundColor: matchColor(favMatch.score).bg }}
                              title={favMatch.reason || ""}
                            >
                              {favMatch.score}% match
                            </span>
                          )}
                          {job.category_label && (
                            <span className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-neutral-500 border border-neutral-300 rounded-full px-2 py-0.5">
                              {job.category_label}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-4 text-xs text-neutral-600">
                          {job.company && <span className="inline-flex items-center gap-1"><Building2 size={11} strokeWidth={1.5} /> {job.company}</span>}
                          {job.location && <span className="inline-flex items-center gap-1"><MapPin size={11} strokeWidth={1.5} /> {job.location}</span>}
                          {job.employment_type && <span className="inline-flex items-center gap-1"><Briefcase size={11} strokeWidth={1.5} /> {job.employment_type}</span>}
                          {fmtSalary(job.salary_min, job.salary_max, job.salary_currency) && (
                            <span className="font-mono text-emerald-700">{fmtSalary(job.salary_min, job.salary_max, job.salary_currency)}</span>
                          )}
                          {job.saved_at && (
                            <span className="inline-flex items-center gap-1 text-neutral-500"><Heart size={10} strokeWidth={1.5} fill="currentColor" /> {new Date(job.saved_at).toLocaleDateString("nl-NL")}</span>
                          )}
                        </div>
                        {favMatch?.reason && (
                          <p className="mt-2 text-xs text-neutral-600 italic leading-relaxed border-l-2 border-neutral-300 pl-3" data-testid={`jobsearch-favorite-match-reason-${i}`}>
                            « {favMatch.reason} »
                          </p>
                        )}
                        {/* Match-Coach — AI-tip die uitlegt welke skill of ervaring
                            de gebruiker kan versterken om deze vacature een
                            hogere match te geven. Beschikbaar op elke bewaarde
                            vacature, ongeacht of de score al berekend is. */}
                        <div
                          className="mt-3"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`jobsearch-favorite-coach-wrap-${i}`}
                        >
                            {!coachTips[job.external_id] ? (
                              <button
                                type="button"
                                data-testid={`jobsearch-favorite-coach-run-${i}`}
                                onClick={() => runCoachTip(job.external_id)}
                                disabled={!!coachBusy[job.external_id]}
                                className={`inline-flex items-center gap-1.5 font-mono text-[0.6rem] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${coachBusy[job.external_id] ? "border-neutral-300 text-neutral-400 cursor-wait" : "border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white"}`}
                                title={t("jsCoachTitle")}
                              >
                                <Sparkles size={11} strokeWidth={1.75} />
                                {coachBusy[job.external_id] ? t("jsCoachThinking") : t("jsCoachRun")}
                              </button>
                            ) : (
                              <div
                                className="mt-1 border-l-4 border-amber-500 bg-amber-50/70 px-3 py-2.5"
                                data-testid={`jobsearch-favorite-coach-tip-${i}`}
                              >
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                  <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-amber-800 inline-flex items-center gap-1.5">
                                    <Sparkles size={11} strokeWidth={1.75} /> {t("jsCoachLabel")}
                                  </p>
                                  <button
                                    type="button"
                                    data-testid={`jobsearch-favorite-coach-refresh-${i}`}
                                    onClick={() => runCoachTip(job.external_id)}
                                    disabled={!!coachBusy[job.external_id]}
                                    className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-amber-700 hover:text-amber-900 underline decoration-dotted"
                                  >
                                    {coachBusy[job.external_id] ? t("jsCoachThinking") : t("jsCoachRefresh")}
                                  </button>
                                </div>
                                {coachTips[job.external_id].focus_skills?.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`jobsearch-favorite-coach-focus-${i}`}>
                                    {coachTips[job.external_id].focus_skills.map((s, k) => (
                                      <span
                                        key={k}
                                        className="font-mono text-[0.6rem] uppercase tracking-[0.12em] px-2 py-0.5 border border-amber-500 bg-white text-amber-800"
                                      >
                                        {s}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {coachTips[job.external_id].advice && (
                                  <p className="text-xs text-neutral-800 mt-1.5 leading-relaxed" data-testid={`jobsearch-favorite-coach-advice-${i}`}>
                                    {coachTips[job.external_id].advice}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                      </div>
                      <div className="shrink-0 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <HideReasonPopover
                          job={job}
                          onHide={hideJob}
                          testId={`jobsearch-favorite-hide-${i}`}
                          className="p-2 border border-neutral-300 text-neutral-400 hover:border-neutral-900 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
                          t={t}
                        />
                        <button
                          type="button"
                          data-testid={`jobsearch-favorite-remove-${i}`}
                          onClick={() => toggleFavorite(job)}
                          className="p-2 border border-rose-500 text-rose-600 bg-rose-50 hover:bg-rose-100 transition-colors"
                          title={t("jsFavRemove")}
                        >
                          <Heart size={13} strokeWidth={1.75} fill="currentColor" />
                        </button>
                        <button
                          data-testid={`jobsearch-favorite-pitch-${i}`}
                          onClick={() => setPitchModalFav({
                            // Favorites are keyed by `external_id` — that
                            // is what backend `/video-letters` expects as
                            // `favorite_id` on both /script and /upload.
                            id: job.external_id || job.id,
                            job_title: job.title,
                            title: job.title,
                            company: job.company,
                            lang: (job.detected_lang || "nl"),
                          })}
                          className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-amber-600 text-amber-800 hover:bg-amber-600 hover:text-white transition-colors px-3 py-2 inline-flex items-center justify-center gap-1.5"
                          title={t("jsFavPitchTitle")}
                        >
                          <Video size={12} strokeWidth={1.75} /> {t("jsFavPitch")}
                        </button>
                        <button
                          data-testid={`jobsearch-favorite-letter-${i}`}
                          onClick={() => addAndWriteLetter(job, `fav-${i}`)}
                          className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 px-3 py-2 inline-flex items-center justify-center gap-1.5"
                        >
                          <PenTool size={12} strokeWidth={1.75} /> {t("jsAddAndLetter")}
                        </button>
                      </div>
                    </div>
                  </li>
                );
                })}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Skip Word Undo — restore dialog. Opens when the user clicks a
          history row whose vacancy was hidden by one or more of their
          skip-words. Offers three CTAs: open only, temp bypass, permanent
          remove. Also always closable via Cancel or backdrop click. */}
      {restoreDialog && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          data-testid="jobsearch-restore-modal"
          onClick={() => setRestoreDialog(null)}
        >
          <div
            className="bg-white max-w-lg w-full shadow-2xl border border-neutral-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-neutral-200">
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500 mb-2">{t("jsRestoreTitle")}</p>
              <p className="font-serif text-xl leading-tight">{restoreDialog.row.job_title || restoreDialog.row.company || ""}</p>
              {restoreDialog.row.company && restoreDialog.row.job_title && (
                <p className="font-mono text-[0.65rem] uppercase tracking-[0.12em] text-neutral-500 mt-1">{restoreDialog.row.company}</p>
              )}
              <p className="text-sm text-neutral-600 mt-3 leading-relaxed">{t("jsRestoreIntro")}</p>
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="jobsearch-restore-matched">
                {restoreDialog.matched.map((kw, j) => (
                  <span key={j} className="font-mono text-[0.6rem] uppercase tracking-[0.12em] px-2 py-0.5 border border-rose-300 bg-rose-50 text-rose-800">
                    {kw}
                  </span>
                ))}
              </div>
            </div>
            <div className="p-6 space-y-3">
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-neutral-500">{t("jsRestoreQuestion")}</p>
              {/* Option 1 — Open only */}
              <button
                type="button"
                data-testid="jobsearch-restore-open"
                onClick={() => {
                  window.open(restoreDialog.row.url, "_blank", "noopener,noreferrer");
                  setRestoreDialog(null);
                }}
                className="w-full text-left border border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 transition-colors p-4"
              >
                <p className="font-mono text-[0.7rem] uppercase tracking-[0.15em] mb-1">{t("jsRestoreOpenOnly")}</p>
                <p className="text-xs opacity-80 leading-relaxed">{t("jsRestoreOpenOnlyHint")}</p>
              </button>
              {/* Option 2 — Temp bypass */}
              <button
                type="button"
                data-testid="jobsearch-restore-temp"
                onClick={() => {
                  const kws = restoreDialog.matched.map((k) => k.toLowerCase());
                  setBypassSkips((prev) => {
                    const next = new Set(prev);
                    for (const kw of kws) next.add(kw);
                    return next;
                  });
                  window.open(restoreDialog.row.url, "_blank", "noopener,noreferrer");
                  const label = kws.length === 1
                    ? t("jsRestoreDoneOne", { kw: kws[0] })
                    : t("jsRestoreDone", { n: kws.length });
                  toast.success(label);
                  setRestoreDialog(null);
                }}
                className="w-full text-left border-2 border-amber-600 bg-amber-50 text-amber-900 hover:bg-amber-100 transition-colors p-4"
              >
                <p className="font-mono text-[0.7rem] uppercase tracking-[0.15em] mb-1">{t("jsRestoreTemp")}</p>
                <p className="text-xs opacity-90 leading-relaxed">{t("jsRestoreTempHint")}</p>
              </button>
              {/* Option 3 — Permanent remove */}
              <button
                type="button"
                data-testid="jobsearch-restore-permanent"
                onClick={async () => {
                  const kwsLc = new Set(restoreDialog.matched.map((k) => k.toLowerCase()));
                  const kept = skipKeywords.filter((k) => !kwsLc.has(k.toLowerCase()));
                  await saveSkipKeywords(kept);
                  window.open(restoreDialog.row.url, "_blank", "noopener,noreferrer");
                  const kws = [...kwsLc];
                  toast.success(kws.length === 1
                    ? t("jsRestoreRemovedOne", { kw: kws[0] })
                    : t("jsRestoreRemovedOne", { kw: kws.join(", ") }));
                  setRestoreDialog(null);
                }}
                className="w-full text-left border border-rose-300 bg-white text-rose-800 hover:bg-rose-50 transition-colors p-4"
              >
                <p className="font-mono text-[0.7rem] uppercase tracking-[0.15em] mb-1">{t("jsRestorePermanent")}</p>
                <p className="text-xs opacity-80 leading-relaxed">{t("jsRestorePermanentHint")}</p>
              </button>
              <button
                type="button"
                data-testid="jobsearch-restore-cancel"
                onClick={() => setRestoreDialog(null)}
                className="w-full font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-900 py-2 mt-2"
              >
                {t("jsRestoreCancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal — opens on card click, shows full description + jump-out
          link + motivatiebrief-CTA (which is the only way to add the job to
          the tracker). */}
      {detailJob && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="jobsearch-detail-modal"
          onClick={() => setDetailJob(null)}
        >
          <div
            className="bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl border border-neutral-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-neutral-200 flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <JobSourceBadge job={detailJob} />
                  {detailJob.__match && (
                    <span
                      className="inline-flex items-center gap-1 font-mono text-[0.6rem] uppercase tracking-[0.12em] text-white rounded-full px-2 py-0.5"
                      style={{ backgroundColor: matchColor(detailJob.__match.score).bg }}
                    >
                      {detailJob.__match.score}% match
                    </span>
                  )}
                  {detailJob.category_label && (
                    <span className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-neutral-500 border border-neutral-300 rounded-full px-2 py-0.5">
                      {detailJob.category_label}
                    </span>
                  )}
                </div>
                <h2 className="font-serif text-2xl leading-tight">{detailJob.title}</h2>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-neutral-600">
                  {detailJob.company && <span className="inline-flex items-center gap-1"><Building2 size={12} strokeWidth={1.5} /> {detailJob.company}</span>}
                  {detailJob.location && <span className="inline-flex items-center gap-1"><MapPin size={12} strokeWidth={1.5} /> {detailJob.location}</span>}
                  {detailJob.employment_type && <span className="inline-flex items-center gap-1"><Briefcase size={12} strokeWidth={1.5} /> {detailJob.employment_type}</span>}
                  {fmtSalary(detailJob.salary_min, detailJob.salary_max, detailJob.salary_currency) && (
                    <span className="font-mono text-emerald-700">{fmtSalary(detailJob.salary_min, detailJob.salary_max, detailJob.salary_currency)}</span>
                  )}
                  {detailJob.posted_at && (
                    <span className="inline-flex items-center gap-1"><Clock size={12} strokeWidth={1.5} /> {new Date(detailJob.posted_at).toLocaleDateString("nl-NL")}</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDetailJob(null)}
                data-testid="jobsearch-detail-close"
                aria-label={t("jsDetailClose")}
                className="text-neutral-500 hover:text-neutral-900 shrink-0 p-1"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {detailJob.__match?.reason && (
                <p className="text-sm text-neutral-600 italic leading-relaxed border-l-2 border-emerald-500 pl-3">
                  « {detailJob.__match.reason} »
                </p>
              )}
              {/* Auto cue-cards — 3-line spiek-script generated by AI so
                  the user has a pocket cheat-sheet before recording. Only
                  shown for favorited jobs; loads on first open then is
                  cached server-side on the favorite doc. */}
              {(() => {
                const extId = detailJob.external_id || detailJob.url;
                if (!extId || !favoriteIds.has(extId)) return null;
                const cards = cueCards[extId];
                const busy = cueCardsBusy[extId];
                if (!cards && !busy) return null;
                return (
                  <div
                    className="border border-amber-300 bg-amber-50/60 p-4"
                    data-testid="jobsearch-detail-cuecards"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-amber-900 inline-flex items-center gap-1.5">
                        <Sparkles size={11} strokeWidth={1.5} /> {t("jsCueCardsTitle")}
                      </p>
                      {cards && (
                        <button
                          type="button"
                          data-testid="jobsearch-detail-cuecards-copy"
                          onClick={() => {
                            navigator.clipboard.writeText(cards.filter(Boolean).join("\n")).then(
                              () => toast.success(t("jsCueCardsCopied")),
                              () => toast.error(t("jsCueCardsCopyFail")),
                            );
                          }}
                          className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-amber-800 hover:text-amber-900 underline decoration-dotted"
                        >
                          {t("jsCueCardsCopy")}
                        </button>
                      )}
                    </div>
                    {busy ? (
                      <p className="text-xs text-neutral-600 italic" data-testid="jobsearch-detail-cuecards-busy">
                        {t("jsCueCardsBusy")}
                      </p>
                    ) : (
                      <ol className="space-y-1.5" data-testid="jobsearch-detail-cuecards-list">
                        {cards.filter(Boolean).map((line, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2.5 text-sm text-neutral-800 leading-relaxed"
                            data-testid={`jobsearch-detail-cuecard-${i}`}
                          >
                            <span className="font-mono text-[0.6rem] uppercase tracking-[0.15em] text-amber-700 mt-1 shrink-0 tabular-nums">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <span>{line}</span>
                          </li>
                        ))}
                      </ol>
                    )}
                    <p className="text-[0.65rem] text-neutral-500 mt-3 font-mono">
                      {t("jsCueCardsHint")}
                    </p>
                  </div>
                );
              })()}
              {detailJob.description ? (
                <div
                  className="jd-body"
                  data-testid="jobsearch-detail-description"
                  dangerouslySetInnerHTML={{
                    __html: (() => {
                      let d = detailJob.description;
                      // If the source ships plain-text with newlines, wrap
                      // paragraphs so our .jd-body spacing kicks in.
                      if (!/<(p|ul|ol|br|h[1-6]|li)\b/i.test(d)) {
                        d = d
                          .split(/\n{2,}/)
                          .map((chunk) => `<p>${chunk.replace(/\n/g, "<br/>")}</p>`)
                          .join("");
                      }
                      return d;
                    })(),
                  }}
                />
              ) : (
                <p className="text-sm text-neutral-500 italic">{t("jsDetailNoBody")}</p>
              )}
            </div>

            <div className="p-6 border-t border-neutral-200 bg-neutral-50 flex flex-wrap gap-3 justify-end">
              <button
                type="button"
                data-testid="jobsearch-detail-fav"
                onClick={() => toggleFavorite(detailJob)}
                className={`font-mono text-[0.65rem] uppercase tracking-[0.15em] border px-3 py-2 inline-flex items-center justify-center gap-1.5 transition-colors ${favoriteIds.has(detailJob.external_id || detailJob.url) ? "border-rose-500 text-rose-700 bg-rose-50" : "border-neutral-300 hover:border-rose-500 hover:text-rose-700"}`}
              >
                <Heart size={12} strokeWidth={1.75} fill={favoriteIds.has(detailJob.external_id || detailJob.url) ? "currentColor" : "none"} />
                {favoriteIds.has(detailJob.external_id || detailJob.url) ? t("jsFavRemove") : t("jsFavAdd")}
              </button>
              {detailJob.url && (
                <a
                  href={detailJob.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="jobsearch-detail-external"
                  className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 hover:border-neutral-900 px-3 py-2 inline-flex items-center justify-center gap-1.5"
                >
                  <ExternalLink size={12} strokeWidth={1.75} /> {t("jsDetailExternal")}
                </a>
              )}
              <button
                type="button"
                data-testid="jobsearch-detail-letter"
                disabled={addedIds.has(detailJob.__idx) || rowBusy === `letter-${detailJob.__idx}`}
                onClick={async () => {
                  await addAndWriteLetter(detailJob, detailJob.__idx);
                  setDetailJob(null);
                }}
                className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-900 bg-neutral-900 text-[#F9F8F6] hover:bg-neutral-700 disabled:opacity-60 disabled:cursor-default px-4 py-2 inline-flex items-center justify-center gap-1.5"
                title={t("jsAddAndLetterHint")}
              >
                <PenTool size={12} strokeWidth={1.75} />
                {addedIds.has(detailJob.__idx) ? t("jsAdded") : t("jsAddAndLetter")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video-pitch modal — opened from the "Video-pitch" button on a
          favorite row. Uses VideoLetterModal in favorite-scoped mode so
          the script prompt is pre-filled with the vacancy context. */}
      {pitchModalFav && (
        <VideoLetterModal
          letter={null}
          favorite={pitchModalFav}
          onClose={() => setPitchModalFav(null)}
        />
      )}
    </div>
  );
}
